import { useEffect, useMemo, useRef, useState } from 'react';
import { snakeCase } from 'lodash';
import { FormCheck, FormSelect, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChatDots, ChatDotsFill, InfoCircle } from 'react-bootstrap-icons';
import { APARTMENT_TYPES } from '../../helpers/constants';
import useLocalization from '../../hooks/useLocalization';
import {
  getNumberFromFormattedPercentage,
  getNumberFromFormattedValue
} from '../admin/manage-users/customer/strategies/helper';
import LinkifiedTextInput, { LinkifiedText } from '../common/LinkifiedTextInput';
import AutoGrowTextArea from '../common/AutoGrowTextArea';
import CompactBlockSelectInput from '../common/CompactBlockSelectInput';
import CurrencyInput from '../common/CurrencyInput';
import CustomMultiSelect from '../common/CustomMultiSelect';
import LTRNumberCell from '../common/LTRNumberCell';
import MultiCheckboxInput from '../common/MultiCheckboxInput';
import PercentageInput from '../common/PercentageInput';
import RangeInput from '../common/RangeInput';
import SwitchInput from '../common/SwitchInput';
import { getPercentageValue, getRemainingSuggestedPaperApartmentPayments } from './helpers';
import Summary from './IrrBreakdownSummary';
import { TableRow, TableCell } from '../ui/table';
import PaperApartmentPayments from './PaperApartmentPayments';
import MonthYearInput from '../common/MonthYearInput';
import CurrencyInputWithUnitDropdown from '../common/CurrencyInputWithUnitDropdown';

const useStickyHover = (hideDelay = 200) => {
  const [show, setShow] = useState(false);
  const hideTimer = useRef(null);

  const open = () => {
    clearTimeout(hideTimer.current);
    setShow(true);
  };

  const close = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShow(false), hideDelay);
  };

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  return { show, hoverProps: { onMouseEnter: open, onMouseLeave: close } };
};

const FieldNoteIcon = ({ note, objectKey, label, isRtlLanguage, translate, onFieldInfoClick }) => {
  const { show, hoverProps } = useStickyHover();

  return (
    <OverlayTrigger
      placement={isRtlLanguage ? 'right' : 'left'}
      trigger={[]}
      show={show}
      overlay={props => (
        <Tooltip
          id={`tooltip-${objectKey}note`}
          {...props}
          {...hoverProps}
          className="field-note-tooltip"
          style={{ ...props.style, pointerEvents: 'auto' }}
        >
          {note ? <LinkifiedText text={note} /> : translate('add_note')}
        </Tooltip>
      )}
    >
      <div className="ms-2" {...hoverProps}>
        {note ? (
          <ChatDotsFill
            size={16}
            cursor="pointer"
            className="text-success"
            onClick={() => onFieldInfoClick({ fieldKey: objectKey, label })}
          />
        ) : (
          <ChatDots size={16} classname="text-gray" onClick={() => onFieldInfoClick({ fieldKey: objectKey, label })} />
        )}
      </div>
    </OverlayTrigger>
  );
};

const DataRow = ({
  label,
  objectKey,
  description,
  rowClassName = 'bg-white',
  extraInfo = null,
  isInput,
  percentageOfField,
  type,
  min,
  max,
  trueValue = 'Yes',
  falseValue = 'No',
  blockMaxWidth = null,
  appartments = [],
  onValueChange,
  allFields,
  isRtlLanguage,
  collapsedApartments,
  tabIndex,
  totalFieldsCount,
  options = [],
  readOnlyMode,
  dataListOptions,
  as,
  required = false,
  apartmentType,
  allowNote,
  notes,
  onFieldInfoClick,
  units
}) => {
  const { translate } = useLocalization();

  const calculatePaperApartmentPayments = (app, addToExistingPayments = false) => {
    let newValues = { ...app };
    //reset paper apartment payments
    newValues.paperApartmentPayments = !addToExistingPayments
      ? []
      : newValues.paperApartmentPayments?.filter(p => !p.isSystemGenerated) || [];

    let newPayments = [];
    if (apartmentType === APARTMENT_TYPES.PAPER) {
      newPayments = [...newValues.paperApartmentPayments, ...getRemainingSuggestedPaperApartmentPayments(newValues)];
    }

    onValueChange(app.id, 'paperApartmentPayments', 'paperApartmentPayments', newPayments);
  };

  const onAutofillRemainingPaymentClick = app => {
    calculatePaperApartmentPayments(app, true);
  };

  const onDownPaymentPercentageChange = (app, value) => {
    const apartmentValue = getNumberFromFormattedValue(app.inputPrice) || 0;
    calculatePaperApartmentPayments({ ...app, downPayment: value }, true);

    onValueChangeInternal(app, 'downPayment', 'number', value);
    onValueChangeInternal(app, 'downPaymentAmount', 'number', value * 0.01 * apartmentValue);
  };

  const onDownPaymentAmountChange = (app, value) => {
    const apartmentValue = getNumberFromFormattedValue(app.inputPrice) || 0;
    const downPaymentPercentage = apartmentValue ? ((value || 0) * 100) / apartmentValue : 0;

    calculatePaperApartmentPayments({ ...app, downPayment: downPaymentPercentage }, true);

    onValueChangeInternal(app, 'downPayment', 'number', downPaymentPercentage);
    onValueChangeInternal(app, 'downPaymentAmount', 'number', value);
  };

  const onValueChangeInternal = (app, objectKey, type, value, additionalInfo) => {
    if (
      ['inputPrice', 'estimatedTimeForFinishingContruction', 'remainingPayment', 'paperApartmentPayments'].includes(
        objectKey
      )
    ) {
      if (objectKey === 'paperApartmentPayments' && additionalInfo?.recalculatePayments) {
        calculatePaperApartmentPayments({ ...app, [objectKey]: value }, true);
        return;
      }

      calculatePaperApartmentPayments({ ...app, [objectKey]: value });
    }
    onValueChange(app.id, objectKey, type, value, additionalInfo);
  };

  let note = useMemo(() => notes?.find(n => n?.fieldKey === objectKey)?.note, [notes, objectKey]);
  const cellColor = rowClassName === 'bg-white' ? 'bg-gray-50' : rowClassName;

  return (
    <TableRow className={`${rowClassName} border-0 ${objectKey === 'irr' ? 'irr-row' : ''}`}>
      <TableCell
        className={`py-1 ps-4 text-sm font-medium ${
          isRtlLanguage ? 'text-right' : 'text-left'
        } calc-column ${cellColor}`}
      >
        <div className="d-flex align-items-center justify-content-between">
          <b>
            {label}
            {required && <span className="text-danger">&nbsp;*</span>}
            {type === 'percentage' && ' (%)'}
            {description && (
              <OverlayTrigger
                placement={!isRtlLanguage ? 'right' : 'left'}
                overlay={<Tooltip id={`tooltip-${objectKey}`}>{description}</Tooltip>}
              >
                <InfoCircle className="mx-2" style={{ cursor: 'pointer' }} />
              </OverlayTrigger>
            )}
          </b>
          {allowNote && (
            <FieldNoteIcon
              note={note}
              objectKey={objectKey}
              label={label}
              isRtlLanguage={isRtlLanguage}
              translate={translate}
              onFieldInfoClick={onFieldInfoClick}
            />
          )}
        </div>
      </TableCell>
      {appartments?.map((app, appIndex) => (
        <TableCell
          key={app.id}
          className={`p-1 ${isRtlLanguage ? 'text-right' : 'text-left'} ${
            !collapsedApartments.includes(app.id) ? 'calc-column' : ''
          }`}
        >
          {!collapsedApartments.includes(app.id) && (
            <div className="flex items-center">
              {isInput ? (
                type === 'block-select' ? (
                  <CompactBlockSelectInput
                    isRtlLanguage={isRtlLanguage}
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                    options={options}
                    blockMaxWidth={blockMaxWidth}
                  />
                ) : type === 'switch' ? (
                  <SwitchInput
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                    trueValue={trueValue}
                    falseValue={falseValue}
                    threeState={true}
                  />
                ) : type === 'range' ? (
                  <RangeInput
                    isRtlLanguage={isRtlLanguage}
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                    min={min}
                    max={max}
                  />
                ) : type === 'boolean' ? (
                  <FormCheck
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    size="sm"
                    disabled={readOnlyMode}
                    checked={app[objectKey]}
                    onChange={e => onValueChangeInternal(app, objectKey, type, e.target.checked)}
                    className="custom-checkbox"
                  />
                ) : type === 'rate' || type === 'dropdown' ? (
                  <FormSelect
                    className="rounded-none"
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    size="sm"
                    disabled={readOnlyMode}
                    value={app[objectKey]}
                    onChange={e =>
                      onValueChangeInternal(
                        app,
                        objectKey,
                        type,
                        e.target.value === 'select_one' ? null : e.target.value
                      )
                    }
                  >
                    {!required && <option value={'select_one'}>{translate('select_one')}</option>}
                    {type === 'rate'
                      ? [
                          'Select...',
                          ...Array(10)
                            .fill(0)
                            .map((a, i) => i + 1)
                        ].map(o => (
                          <option key={o} value={o === 'Select...' ? '' : o}>
                            {o}
                          </option>
                        ))
                      : options.map(({ label, value }) => (
                          <option key={value} value={value}>
                            {label || translate(snakeCase(value)) || value}
                          </option>
                        ))}
                  </FormSelect>
                ) : type === 'grouped-dropdown' ? (
                  <CustomMultiSelect
                    disabled={readOnlyMode}
                    items={options}
                    selectedItems={app[objectKey]}
                    onChange={option => onValueChangeInternal(app, objectKey, type, option)}
                    isMulti={false}
                    isGroupped
                    closeMenuOnSelect={true}
                    placeholder={translate('select_one')}
                    containerWidth="100%"
                  />
                ) : type === 'percentage' ? (
                  <PercentageInput
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    className="m-0"
                    size="sm"
                    disabled={readOnlyMode}
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                  />
                ) : type === 'paperApartmentPayments' ? (
                  <PaperApartmentPayments
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    disabled={readOnlyMode}
                    paperApartmentPayments={app[objectKey]}
                    apartmentBuyMonth={0}
                    apartmentValue={getNumberFromFormattedValue(app.inputPrice) || 0}
                    downPaymentPercentage={getNumberFromFormattedPercentage(app.downPayment) || 0}
                    downPaymentAmount={app.downPaymentAmount}
                    onAutofillRemainingPaymentClick={() => onAutofillRemainingPaymentClick(app)}
                    onDownPaymentPercentageChange={value => onDownPaymentPercentageChange(app, value)}
                    onDownPaymentAmountChange={value => onDownPaymentAmountChange(app, value)}
                    onPaymentsChange={(value, recalculatePayments = true) =>
                      onValueChangeInternal(app, objectKey, type, value, { recalculatePayments })
                    }
                    readOnlyMode={readOnlyMode}
                    monthsInAbsoluteTerms={true}
                  />
                ) : type === 'linkifiedText' ? (
                  <LinkifiedTextInput
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    disabled={readOnlyMode}
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                  />
                ) : type === 'text' ? (
                  <>
                    <AutoGrowTextArea
                      autoComplete="off"
                      tabIndex={appIndex * totalFieldsCount + tabIndex}
                      className="m-0"
                      as={as || (percentageOfField ? 'input' : 'textarea')}
                      type={'text'}
                      style={percentageOfField ? { width: '70%' } : {}}
                      size="sm"
                      disabled={readOnlyMode}
                      rows={1}
                      list={`${objectKey}-list`}
                      value={app[objectKey]}
                      onChange={e => onValueChangeInternal(app, objectKey, type, e.target.value, { percentageOfField })}
                    />
                    {dataListOptions?.length > 0 && (
                      <datalist id={`${objectKey}-list`}>
                        {dataListOptions.map(option => (
                          <option key={option} value={option} />
                        ))}
                      </datalist>
                    )}
                  </>
                ) : type === 'multi-checkbox' ? (
                  <MultiCheckboxInput
                    options={options}
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                  />
                ) : type === 'monthYearInput' ? (
                  <MonthYearInput
                    isRtlLanguage={isRtlLanguage}
                    value={app[objectKey]}
                    onChange={value => onValueChangeInternal(app, objectKey, type, value)}
                  />
                ) : percentageOfField ? (
                  <CurrencyInputWithUnitDropdown
                    isRtlLanguage={isRtlLanguage}
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    className="m-0"
                    size="sm"
                    disabled={readOnlyMode}
                    value={app[objectKey]}
                    percentValue={app[`${objectKey}-percentage`]}
                    onChange={number => onValueChangeInternal(app, objectKey, type, number, { percentageOfField })}
                    onChangePercent={value =>
                      onValueChangeInternal(app, objectKey, 'dependent-percentage', value, {
                        percentageOfField
                      })
                    }
                  />
                ) : (
                  <CurrencyInput
                    tabIndex={appIndex * totalFieldsCount + tabIndex}
                    className="m-0"
                    style={percentageOfField ? { width: '70%' } : {}}
                    size="sm"
                    disabled={readOnlyMode}
                    value={app[objectKey]}
                    onChange={number => onValueChangeInternal(app, objectKey, type, number, { percentageOfField })}
                  />
                )
              ) : (
                <div className="flex items-center px-2 py-1 w-full text-sm border border-gray-100 bg-gray-100 text-black">
                  <LTRNumberCell
                    value={
                      type === 'percentage' ? getPercentageValue(allFields, objectKey, app[objectKey]) : app[objectKey]
                    }
                    formatType={type === 'percentage' ? 'percentage' : 'currency'}
                  />
                </div>
              )}
              {extraInfo && app?.[extraInfo?.key] && <Summary data={app?.[extraInfo?.key]} extraInfo={extraInfo} />}
            </div>
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default DataRow;
